import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaEnvelope, FaIdCard, FaPhone, FaLock, FaSpinner, FaGraduationCap, FaKey, FaEye, FaEyeSlash } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { activateStudentAccount } from '../../services/learnService';
import SEO from '../../components/common/SEO';
import PageSection from '../../components/common/PageSection';

const Field = ({ id, label, icon: Icon, hint, ...props }) => {
  const [show, setShow] = useState(false);
  const isPassword = props.type === 'password';
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          id={id}
          name={id}
          className={`w-full pl-10 ${isPassword ? 'pr-11' : 'pr-4'} py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-designer-primary/30 focus:border-designer-primary transition-all duration-200`}
          {...props}
          type={isPassword && show ? 'text' : props.type}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            aria-label={show ? 'Hide password' : 'Show password'}
            aria-pressed={show}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-designer-primary transition-colors"
          >
            {show ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
          </button>
        )}
      </div>
      {hint && <p className="text-xs text-gray-400 mt-1.5">{hint}</p>}
    </div>
  );
};

/**
 * Creative Samurai students activate their Academy account (or reset a
 * forgotten password) by proving their enrollment: email + Student ID + phone.
 */
const Activate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({
    email: location.state?.email || '',
    student_id: '',
    phone: '',
    password: '',
    confirm: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error('Choose a password with at least 8 characters');
      return;
    }
    if (form.password !== form.confirm) {
      toast.error('The two passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      const { token, user } = await activateStudentAccount({
        email: form.email,
        student_id: form.student_id,
        phone: form.phone,
        password: form.password,
      });
      login(token, user);
      toast.success(`Welcome to Arvix Academy, ${user.name}!`);
      navigate(location.state?.from || '/learn/creative-samurai', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Activation failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO title="Activate your student account | Arvix Academy" description="Creative Samurai students: activate your Arvix Academy account to watch recorded classes." />
      <PageSection className="pt-24 sm:pt-28 md:pt-32 pb-12 md:pb-16">
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="w-full max-w-md">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-red-100/50 shadow-lg p-6 sm:p-8 md:p-10">
              <div className="text-center mb-7">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-100 text-designer-primary text-xs font-bold tracking-widest mb-5">
                  <FaGraduationCap className="w-3.5 h-3.5" />
                  CREATIVE SAMURAI STUDENTS
                </div>
                <h1 className="text-2xl font-bold text-designer-dark">Activate your account</h1>
                <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                  Use the details from your enrollment to set your password. Forgot your password? Do the same to reset it.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-4">
                <Field id="email" label="Enrollment email" icon={FaEnvelope} type="email" autoComplete="email" required value={form.email} onChange={onChange} placeholder="you@example.com" />
                <Field id="student_id" label="Student ID" icon={FaIdCard} required value={form.student_id} onChange={onChange} placeholder="CS-0001" hint="Printed on your Creative Samurai student ID card." autoCapitalize="characters" />
                <Field id="phone" label="Phone number" icon={FaPhone} type="tel" autoComplete="tel" required value={form.phone} onChange={onChange} placeholder="01XXXXXXXXX" hint="The number you enrolled with." />
                <Field id="password" label="New password" icon={FaLock} type="password" autoComplete="new-password" required minLength={8} value={form.password} onChange={onChange} placeholder="At least 8 characters" />
                <Field id="confirm" label="Confirm password" icon={FaLock} type="password" autoComplete="new-password" required minLength={8} value={form.confirm} onChange={onChange} placeholder="Type it again" />

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-gradient-to-r from-designer-primary to-red-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-red-500/30 transition-all duration-300 disabled:opacity-60"
                >
                  {isLoading ? <><FaSpinner className="animate-spin w-4 h-4" /> Activating…</> : <><FaKey className="w-4 h-4" /> Activate &amp; watch classes</>}
                </button>
              </form>

              <p className="text-center text-sm text-gray-500 mt-6">
                Already activated?{' '}
                <Link to="/login" className="text-designer-primary font-semibold hover:underline">Sign in</Link>
              </p>
              <p className="text-center text-xs text-gray-400 mt-3">
                Details not matching? Message Arvix Creative on WhatsApp 01645-076455.
              </p>
            </div>
          </div>
        </div>
      </PageSection>
    </>
  );
};

export default Activate;
